// Play phase. docs/fe-state-machine.md#play and docs/gameplay-config.md.
//
// One canvas, one RAF (driven by the state machine), one set of input
// listeners. All game state lives in module scope while the phase is active
// and is reset on enter() so Replay (End → Play) starts fresh.
//
// Gameplay tunables (play duration, gravity, spawn timing, item radius/visualSize,
// character collider) come from shared.config.gamePlay — see /v1/config.

import type { Phase, TransitionFn } from "../state/types";
import { gameFinish } from "../api/client";
import { getSignature } from "../crypto";
import {
  COUNTDOWN_WARNING_AT_S,
  SCORE_FLOOR,
  SCORE_PER_HAZARD,
  SCORE_PER_INGREDIENT,
} from "../config/gameplay";
import { SCENE_HEIGHT, SCENE_WIDTH } from "../config/scene";
import { mount, clearChildren } from "../ui/overlay";
import { getAudioBus } from "../audio";
import {
  applyScale,
  characterColliderPosition,
  clientToScene,
  computeScale,
  drawBackground,
  drawCharacter,
  drawColliderDebug,
  drawItems,
  drawParticles,
} from "../game/renderer";
import { createSpawner, tickSpawner } from "../game/spawner";
import type { SpawnerState } from "../game/spawner";
import { itemY } from "../game/physics";
import type { FallingItem } from "../game/physics";
import { circlesOverlap } from "../game/collision";
import { createCharacter, setCharacterEmotion, updateCharacter } from "../game/character";
import type { CharacterState } from "../game/character";
import { pruneParticles, spawnBurst } from "../game/particles";
import type { Particle } from "../game/particles";

function debugOverlayEnabled(): boolean {
  return new URLSearchParams(location.search).has("debug");
}

let cleanup: (() => void) | null = null;
let canvas: HTMLCanvasElement | null = null;
let timeEl: HTMLElement | null = null;
let pointerMoveListener: ((e: PointerEvent) => void) | null = null;
let pointerDownListener: ((e: PointerEvent) => void) | null = null;
let resizeListener: (() => void) | null = null;
let liveTransition: TransitionFn | null = null;

let elapsed = 0;
let items: FallingItem[] = [];
let particles: Particle[] = [];
let character: CharacterState = createCharacter();
let spawner: SpawnerState | null = null;
let ended = false;
let liveContext: import("../state/types").Context | null = null;
let livePlayTime: number | null = null;

export const Play: Phase = {
  enter(ctx, transition) {
    elapsed = 0;
    ended = false;
    items = [];
    particles = [];
    character = createCharacter();
    spawner = null;
    ctx.score = 0;
    liveTransition = transition;
    liveContext = ctx;
    livePlayTime = null;

    const root = document.getElementById("app")!;
    clearChildren(root);

    const wrap = document.createElement("div");
    wrap.style.cssText = "position:absolute;inset:0;background:#000;overflow:hidden;";

    canvas = document.createElement("canvas");
    canvas.style.cssText =
      "position:absolute;inset:0;width:100%;height:100%;display:block;touch-action:none;";
    wrap.appendChild(canvas);

    // HUD: time board (top-left) + sound-toggle & skip buttons (top-right),
    // using the flow art. The timer number is overlaid in the board's right-hand
    // area (icon is baked in). No score board — the game is win-only (client).
    const FLOW = "/textures/flow";
    const hud = document.createElement("div");
    hud.style.cssText =
      `position:absolute;top:env(safe-area-inset-top,12px);left:0;right:0;display:flex;justify-content:space-between;align-items:flex-start;padding:0 3vw;pointer-events:none;font-variant-numeric:tabular-nums;`;
    const numStyle =
      "position:absolute;right:12%;top:50%;transform:translateY(-50%);font-size:5vw;font-weight:bold;color:#fff;text-shadow:0 1px 3px #0009;";
    const hudBtn =
      "width:12vw;max-width:60px;aspect-ratio:1;border:0;padding:0;cursor:pointer;background-color:transparent;background-position:center;background-size:contain;background-repeat:no-repeat;";
    hud.innerHTML = `
      <div style="position:relative;width:34vw;max-width:200px;aspect-ratio:652/228;background:url(${FLOW}/timeBoard.png) center/contain no-repeat;">
        <div id="play-time" style="${numStyle}">--</div>
      </div>
      <div style="display:flex;gap:2.5vw;pointer-events:auto;">
        <button id="play-sound" aria-label="Bật/tắt âm thanh" style="position:relative;${hudBtn}background-image:url(${FLOW}/btnSound.png);">
          <span id="play-sound-strike" style="position:absolute;left:14%;top:46%;width:72%;height:8%;background:#e11;border-radius:2px;box-shadow:0 0 0 1px #fff;transform:rotate(45deg);transform-origin:center;display:none;"></span>
        </button>
        <button id="play-skip" aria-label="Bỏ qua trò chơi" style="${hudBtn}background-image:url(${FLOW}/btnSkip.png);"></button>
      </div>
    `;
    wrap.appendChild(hud);

    cleanup = mount(root, wrap);
    timeEl = hud.querySelector<HTMLElement>("#play-time");

    // Asset readiness gate.
    const shared = ctx.assets.shared;
    if (!shared || !ctx.assets.characterSprites) {
      console.warn("[Play] missing shared/character assets; routing to Result");
      transition("Result");
      return;
    }

    const gp = shared.config.gamePlay;
    spawner = createSpawner(gp.spawnTime);
    livePlayTime = gp.playTime;
    if (timeEl) timeEl.textContent = gp.playTime.toFixed(0);

    // Sound toggle (mute persists for the session via the AudioBus singleton)
    // + skip (ends the game immediately → win Result).
    const soundBtn = hud.querySelector<HTMLButtonElement>("#play-sound")!;
    const soundStrike = hud.querySelector<HTMLElement>("#play-sound-strike")!;
    const reflectMute = (m: boolean) => {
      soundBtn.style.opacity = m ? "0.55" : "1";
      soundStrike.style.display = m ? "block" : "none";
    };
    reflectMute(getAudioBus().isMuted);
    soundBtn.addEventListener("click", () => reflectMute(getAudioBus().toggleMute()));
    hud
      .querySelector<HTMLButtonElement>("#play-skip")!
      .addEventListener("click", () => finishGame(shared.audio.endGame, true));

    // Sizing.
    const resize = () => {
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
    };
    resize();
    resizeListener = resize;
    window.addEventListener("resize", resize);

    // Input: pointermove + pointerdown.
    const onMove = (e: PointerEvent) => {
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      const s = computeScale(window.innerWidth * dpr, window.innerHeight * dpr);
      const sceneX = clientToScene(e.clientX * dpr, s);
      character.targetX = Math.max(0, Math.min(sceneX, SCENE_WIDTH));
    };
    const onDown = (e: PointerEvent) => {
      onMove(e);
      character.x = character.targetX; // snap so first-touch doesn't lerp from center
    };
    pointerMoveListener = onMove;
    pointerDownListener = onDown;
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerdown", onDown);

    // Audio: resume (defensive — user gestured in Boot), start BGM.
    const bus = getAudioBus();
    void bus.resume().then(() => bus.playBGM(shared.audio.bgm));
  },

  render(dt, ctx) {
    if (ended) return;
    elapsed += dt;
    const now = elapsed;

    const shared = ctx.assets.shared;
    const sprites = ctx.assets.characterSprites;
    if (!shared || !sprites || !canvas || !spawner) return;
    const config = shared.config;
    const gp = config.gamePlay;

    // --- Spawn ---
    const newItem = tickSpawner(
      spawner,
      dt,
      items,
      { ingredientCount: shared.ingredients.length, hazardCount: shared.hazards.length },
      now,
      gp.spawnTime,
      gp.Item.visualSize,
    );
    if (newItem) items.push(newItem);

    // --- Items: despawn off-screen, otherwise collide ---
    const charPos = characterColliderPosition(character.x, config);
    const charR = gp.colliders.character.radius;
    const itemR = gp.Item.radius;
    const survivors: FallingItem[] = [];
    for (const item of items) {
      const y = itemY(item, now, gp.gravity, gp.Item.visualSize);
      if (y > SCENE_HEIGHT + 200) continue;
      if (circlesOverlap(charPos.x, charPos.y, charR, item.x, y, itemR)) {
        if (item.kind === "ingredient") {
          ctx.score += SCORE_PER_INGREDIENT;
          setCharacterEmotion(character, "happy");
          getAudioBus().playSFX(shared.audio.collectIngredient);
          spawnBurst(particles, item.x, y, "ingredient", now);
        } else {
          ctx.score = Math.max(SCORE_FLOOR, ctx.score + SCORE_PER_HAZARD);
          setCharacterEmotion(character, "sad");
          getAudioBus().playSFX(shared.audio.collectHazard);
          spawnBurst(particles, item.x, y, "hazard", now);
        }
        continue;
      }
      survivors.push(item);
    }
    items = survivors;

    updateCharacter(character, dt);
    pruneParticles(particles, now);

    // --- Draw ---
    const c = canvas.getContext("2d");
    if (!c) return;
    c.setTransform(1, 0, 0, 1, 0, 0);
    c.clearRect(0, 0, canvas.width, canvas.height);
    const s = computeScale(canvas.width, canvas.height);
    applyScale(c, s);

    drawBackground(c, shared);
    drawItems(c, shared, items, now, gp);
    drawCharacter(c, shared, sprites, character, config.characterVisual);
    drawParticles(c, shared, particles, now);
    if (debugOverlayEnabled()) drawColliderDebug(c, character, config, items, now);

    // --- HUD ---
    const remaining = Math.max(0, gp.playTime - elapsed);
    if (timeEl) {
      timeEl.textContent = remaining.toFixed(remaining < 10 ? 1 : 0);
      timeEl.style.color = remaining <= COUNTDOWN_WARNING_AT_S ? "#f55" : "#fff";
    }

    if (remaining <= 0) finishGame(shared.audio.endGame, false);
  },

  exit() {
    if (canvas) {
      if (pointerMoveListener) canvas.removeEventListener("pointermove", pointerMoveListener);
      if (pointerDownListener) canvas.removeEventListener("pointerdown", pointerDownListener);
    }
    if (resizeListener) window.removeEventListener("resize", resizeListener);
    pointerMoveListener = null;
    pointerDownListener = null;
    resizeListener = null;
    liveContext = null;
    livePlayTime = null;
    cleanup?.();
    cleanup = null;
    canvas = null;
    timeEl = null;
    liveTransition = null;
    try {
      getAudioBus().stopBGM();
    } catch {
      // bus may not be initialized in test contexts
    }
  },
};

function finishGame(endGameSfx: AudioBuffer, skipped: boolean): void {
  if (ended) return;
  ended = true;
  const bus = getAudioBus();
  bus.playSFX(endGameSfx);
  bus.stopBGM();
  // Gọi API finish để lưu thời gian kết thúc
  if (liveContext?.sessionId) {
    const playTimeS = skipped ? elapsed : (livePlayTime ?? 20);
    getSignature(liveContext.sessionId).then((sig) => {
      gameFinish(sig, playTimeS).catch((e) =>
        console.warn("[Play] gameFinish failed", e)
      );
    });
  }
  liveTransition?.("Result");
}
